import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import { getProductsPurchasePrice } from 'interfaces/products';
import { createPricing, getPricing, updateSpecificPricing } from 'interfaces/pricing';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { MdSave } from 'react-icons/md';
import { RxDragHandleDots2 } from 'react-icons/rx';
import { getTotalHT } from 'views/orders/createOrders/utils';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const LotInput = ({
    item,
    index,
    mobile = false,
    products,
    isDisabled,
    handleLotNumberChange,
}: {
    item: any;
    index: number;
    mobile?: boolean;
    products: any;
    isDisabled: boolean;
    handleLotNumberChange: (item: any, value: string) => void;
}) => {
    const fullProduct = products?.find((p: any) => p._id === item.productId);
    const uniqueLots = Array.from(new Set(
        (fullProduct?.inventory ?? [])
            .filter((inv: any) => inv.lotNumber && inv.quantity > 0)
            .map((inv: any) => inv.lotNumber as string)
    )) as string[];
    const datalistId = `lot-${mobile ? 'm-' : ''}${item.productId}-${index}`;
    const [localValue, setLocalValue] = useState<string>(item.lotNumber || '');
    const isPicking = useRef(false);

    useEffect(() => {
        setLocalValue(item.lotNumber || '');
    }, [item.lotNumber]);

    return (
        <>
            <input
                type="text"
                list={uniqueLots.length > 0 ? datalistId : undefined}
                className="h-[26px] w-[90px] border rounded-lg px-2 text-xs text-gray-600 focus:outline-none focus:ring focus:border-blue-300"
                value={localValue}
                placeholder="N° lot"
                disabled={isDisabled}
                onChange={(e) => {
                    const val = e.target.value;
                    setLocalValue(val);
                    if (uniqueLots.includes(val)) {
                        isPicking.current = true;
                        handleLotNumberChange(item, val);
                    }
                }}
                onBlur={(e) => {
                    if (!isPicking.current) {
                        handleLotNumberChange(item, e.target.value);
                    }
                    isPicking.current = false;
                }}
            />
            {uniqueLots.length > 0 && (
                <datalist id={datalistId}>
                    {uniqueLots.map((lot, i) => <option key={i} value={lot} />)}
                </datalist>
            )}
        </>
    );
};

/* ── Sortable row wrapper for desktop ── */
const SortableTableRow = ({
    id,
    children,
    isDisabled,
    rowRef,
    className,
}: {
    id: string;
    children: React.ReactNode;
    isDisabled: boolean;
    rowRef: (el: HTMLTableRowElement | null) => void;
    className: string;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: isDragging ? 'relative' : undefined,
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <tr
            ref={(el: HTMLTableRowElement | null) => {
                setNodeRef(el);
                rowRef(el);
            }}
            style={style}
            className={className}
        >
            <td className="px-2 py-2 w-[28px]">
                {!isDisabled && (
                    <span
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 select-none flex items-center justify-center"
                        title="Réordonner"
                    >
                        <RxDragHandleDots2 size={16} />
                    </span>
                )}
            </td>
            {children}
        </tr>
    );
};

const OrderTable = (props: {
    order: any;
    handleQtyChange: any;
    handlePriceChange: any;
    handleDiscountTypeChange: any;
    handleDiscountChange: any;
    handleLotNumberChange: any;
    handleCommentChange: any;
    handlePackagingChange: any;
    onDelete: any;
    onReorder?: (items: any[]) => void;
    products: any;
    customerId?: string;
    searchQuery?: string;
}) => {
    const {
        order,
        handleQtyChange,
        handlePriceChange,
        onDelete,
        onReorder,
        handleDiscountChange,
        handleDiscountTypeChange,
        handleCommentChange,
        handleLotNumberChange,
        handlePackagingChange,
        products,
        customerId,
        searchQuery = '',
    } = props;

    const [isCommentSectionOpen, setIsCommentSectionOpen] = useState<any>({});
    const [refreshableOrder, setRefreshableOrder] = useState<any>(order);
    const [savedCustomPrices, setSavedCustomPrices] = useState<Set<string>>(new Set());
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
    const mobileRowRefs = useRef<(HTMLDivElement | null)[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    useEffect(() => {
        if (!searchQuery.trim()) return;
        const q = searchQuery.trim().toLowerCase();
        const idx = refreshableOrder.items.findIndex((item: any) =>
            (item.name || '').toLowerCase().includes(q) ||
            (item.ref || '').toLowerCase().includes(q)
        );
        if (idx !== -1) {
            const desktopRow = rowRefs.current[idx];
            const mobileRow = mobileRowRefs.current[idx];
            const target = desktopRow || mobileRow;
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [searchQuery]);

    const handleSaveCustomPrice = async (item: any) => {
        if (!customerId) return;
        const fullProduct = products?.find((p: any) => p._id === item.productId);
        if (fullProduct?.customPrice) {
            const pricingList = await getPricing(customerId, 1, 1000);
            const existing = pricingList?.data?.find(
                (p: any) => p.productId === item.productId || p.productId?.toString() === item.productId?.toString()
            );
            if (existing) {
                const res = await updateSpecificPricing(existing._id, {
                    productId: item.productId,
                    price: parseFloat(item.price),
                    tags: existing.tags,
                });
                if (res) {
                    setSavedCustomPrices(prev => new Set([...prev, item.productId]));
                    toast.success(`Prix personnalisé mis à jour pour ${item.name}`);
                }
                return;
            }
        }
        const res = await createPricing({
            productId: item.productId,
            customerId,
            price: parseFloat(item.price),
        });
        if (res) {
            setSavedCustomPrices(prev => new Set([...prev, item.productId]));
            toast.success(`Prix personnalisé enregistré pour ${item.name}`);
        }
    };

    const initProductsPrice = async () => {
        const productIds = order.items.map((item: any) => item.productId);
        if (productIds.length === 0) return;
        const res = await getProductsPurchasePrice(productIds);
        if (!res) return;
        const newItems = order.items.map((item: any) => {
            const product = res.data.find((p: any) => p.id === item.productId);
            if (product) item.totalPurchasePrice = product?.marginData?.purchasePrice;
            return item;
        });
        order.items = newItems;
        setRefreshableOrder((prev: any) => ({ ...prev, items: newItems }));
    };

    useEffect(() => {
        setRefreshableOrder(order);
        initProductsPrice();
    }, [order]);

    const getMarginPct = (item: any, sellPrice: number) => {
        const totalCost = item.totalPurchasePrice * item.quantity;
        if (!totalCost || !sellPrice) return null;
        return `${(((sellPrice - totalCost) / sellPrice) * 100).toFixed(1)} %`;
    };

    const isDisabled = !(order && order.invoiced === false);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = parseInt((active.id as string).replace('item-', ''));
        const newIndex = parseInt((over.id as string).replace('item-', ''));
        if (!isNaN(oldIndex) && !isNaN(newIndex)) {
            const newItems = arrayMove([...refreshableOrder.items], oldIndex, newIndex);
            onReorder?.(newItems);
        }
    };

    const moveMobileItem = (index: number, direction: -1 | 1) => {
        const newItems = [...refreshableOrder.items];
        const target = index + direction;
        if (target < 0 || target >= newItems.length) return;
        [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
        onReorder?.(newItems);
    };

    /* ── Sub-components ── */

    const PackagingSelect = ({ item }: { item: any }) => {
        const fullProduct = products?.find((p: any) => p._id === item.productId);
        if (!fullProduct?.packagings?.length)
            return <span className="text-xs text-gray-600">{item.unity || '—'}</span>;
        const currentIndex = item.packagingIndex != null
            ? item.packagingIndex
            : item.packagingRef
            ? fullProduct.packagings.findIndex((p: any) => p.ref === item.packagingRef)
            : item.packagingUnity
            ? fullProduct.packagings.findIndex((p: any) => p.unity === item.packagingUnity)
            : -1;
        return (
            <select
                className="h-[26px] px-2 text-xs border border-gray-200 rounded-lg bg-white max-w-[140px]"
                value={currentIndex >= 0 ? currentIndex.toString() : ''}
                onChange={(e) => {
                    if (e.target.value === '') handlePackagingChange(item, null, undefined);
                    else {
                        const idx = parseInt(e.target.value);
                        handlePackagingChange(item, fullProduct.packagings[idx], idx);
                    }
                }}
                disabled={isDisabled}
            >
                <option value="">{fullProduct.unity} – {fullProduct.price} €</option>
                {fullProduct.packagings.map((pkg: any, idx: number) => (
                    <option key={idx} value={idx.toString()}>
                        {pkg.unity} ({pkg.weight} {fullProduct.unity}) – {pkg.price} €
                    </option>
                ))}
            </select>
        );
    };

    const SavePriceHint = ({ item }: { item: any }) => {
        const fullProduct = products?.find((p: any) => p._id === item.productId);
        const alreadySaved = savedCustomPrices.has(item.productId);
        if (alreadySaved) return <span className="text-[10px] text-green-500">✓ Enregistré</span>;
        const priceChanged = fullProduct && parseFloat(item.price) !== parseFloat(fullProduct.price);
        if (!priceChanged || !customerId) return null;
        return (
            <button
                className="flex items-center gap-0.5 text-[10px] text-toola-500 hover:text-toola-600"
                onClick={() => handleSaveCustomPrice(item)}
            >
                <MdSave size={10} />
                {fullProduct?.customPrice ? 'Màj prix perso.' : 'Enregistrer prix perso.'}
            </button>
        );
    };

    const sortableIds = refreshableOrder.items.map((_: any, i: number) => `item-${i}`);

    return (
        <div className="w-full mt-2">

            {/* ── Desktop table ── */}
            <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                            {/* Drag handle column */}
                            <th className="px-2 py-2 w-[28px]" />
                            {['Produit', 'Conditionnement', 'Quantité', 'Prix unit. HT', 'Remise', 'Total HT'].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                                    {h}
                                </th>
                            ))}
                            {!isDisabled && <th className="px-3 py-2 w-[60px]" />}
                        </tr>
                    </thead>
                    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                            <tbody className="divide-y divide-gray-50">
                                {refreshableOrder.items.map((item: any, index: number) => {
                                    const q = searchQuery.trim().toLowerCase();
                                    const isMatch = q !== '' && (
                                        (item.name || '').toLowerCase().includes(q) ||
                                        (item.ref || '').toLowerCase().includes(q)
                                    );
                                    return (
                                    <SortableTableRow
                                        key={`${item.productId}-${index}`}
                                        id={`item-${index}`}
                                        isDisabled={isDisabled}
                                        rowRef={(el) => { rowRefs.current[index] = el; }}
                                        className={`hover:bg-gray-50/60 transition-colors ${isMatch ? 'bg-yellow-50 ring-1 ring-inset ring-yellow-300' : ''}`}
                                    >

                                        {/* Produit */}
                                        <td className="px-3 py-2 max-w-[220px]">
                                            <p className="text-sm font-medium text-navy-700 dark:text-white truncate leading-tight">{item.name}</p>
                                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                <span className="text-[11px] text-gray-600">{item.ref}</span>
                                                <LotInput item={item} index={index} products={products} isDisabled={isDisabled} handleLotNumberChange={handleLotNumberChange} />
                                            </div>
                                            {(isCommentSectionOpen[`${item.name}-${item.ref}`] || item.comment) ? (
                                                <div className="mt-1">
                                                    <textarea
                                                        className="w-full h-8 px-2 py-1 text-xs border rounded-lg focus:outline-none focus:ring focus:border-blue-300 resize-none"
                                                        placeholder="Commentaire..."
                                                        value={item.comment || ''}
                                                        onChange={(e) => handleCommentChange(item, e.target.value)}
                                                    />
                                                    <button
                                                        className={`text-[10px] ${isDisabled ? 'invisible' : 'text-red-400 hover:text-red-500'}`}
                                                        onClick={() => {
                                                            if (isDisabled) return;
                                                            setIsCommentSectionOpen({ ...isCommentSectionOpen, [`${item.name}-${item.ref}`]: false });
                                                            handleCommentChange(item, '');
                                                        }}
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    className={`text-[10px] ${isDisabled ? 'invisible' : 'text-toola-400 hover:text-toola-500'}`}
                                                    onClick={() => {
                                                        if (isDisabled) return;
                                                        setIsCommentSectionOpen({ ...isCommentSectionOpen, [`${item.name}-${item.ref}`]: true });
                                                    }}
                                                >
                                                    + commentaire
                                                </button>
                                            )}
                                        </td>

                                        {/* Conditionnement */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <PackagingSelect item={item} />
                                        </td>

                                        {/* Quantité */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {(() => {
                                                const fullProduct = products?.find((p: any) => p._id === item.productId);
                                                const isBillByBase = item.packagingBillByBaseUnit === true && item.packagingWeight;
                                                const weight = isBillByBase ? parseFloat(item.packagingWeight) : 1;
                                                const displayQty = isBillByBase
                                                    ? parseFloat(item.quantity) * weight
                                                    : item.quantity;
                                                return (
                                                    <div>
                                                        <div className="flex items-center gap-1">
                                                            <InputField
                                                                extra="w-[60px]"
                                                                placeholder="Qté"
                                                                id={`qty-input-${index}`}
                                                                type="number"
                                                                value={isBillByBase ? (displayQty % 1 === 0 ? displayQty : parseFloat(displayQty.toFixed(4))) : item.quantity}
                                                                onChange={(e: any) => {
                                                                    if (isBillByBase) {
                                                                        const baseVal = parseFloat(e.target.value);
                                                                        handleQtyChange(item, isNaN(baseVal) ? 0 : baseVal / weight);
                                                                    } else {
                                                                        handleQtyChange(item, e.target.value);
                                                                    }
                                                                }}
                                                                onClick={(e: any) => e.target.select()}
                                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                    if (e.key === 'ArrowDown') {
                                                                        e.preventDefault();
                                                                        const next = document.getElementById(`qty-input-${index + 1}`) as HTMLInputElement | null;
                                                                        if (next) { next.focus(); setTimeout(() => next.select(), 0); }
                                                                    } else if (e.key === 'ArrowUp') {
                                                                        e.preventDefault();
                                                                        const prev = document.getElementById(`qty-input-${index - 1}`) as HTMLInputElement | null;
                                                                        if (prev) { prev.focus(); setTimeout(() => prev.select(), 0); }
                                                                    }
                                                                }}
                                                                disabled={isDisabled}
                                                                inputSmall={true}
                                                            />
                                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[65px]">
                                                                {isBillByBase ? fullProduct?.unity : (item.packagingUnity || item.unity)}
                                                            </span>
                                                        </div>
                                                        {isBillByBase && (
                                                            <p className="text-[10px] text-gray-500 mt-0.5">
                                                                {item.quantity} {item.packagingUnity} commandés
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>

                                        {/* Prix unitaire */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                <InputField
                                                    extra="w-[80px]"
                                                    placeholder="Prix"
                                                    id={item.name + '_up'}
                                                    type="number"
                                                    value={item.price}
                                                    onChange={(e: any) => handlePriceChange(item, e.target.value)}
                                                    disabled={isDisabled}
                                                    inputSmall={true}
                                                />
                                                <span className="text-xs text-gray-600">€</span>
                                            </div>
                                            <SavePriceHint item={item} />
                                        </td>

                                        {/* Remise */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                <div className="w-[72px]">
                                                    <SearchSelector
                                                        displayKey="name"
                                                        value={item.discountType || 'none'}
                                                        options={[
                                                            { name: 'Aucune', _id: 'none' },
                                                            { name: '%', _id: 'percent' },
                                                            { name: '€', _id: 'value' },
                                                        ]}
                                                        onSelect={(e: any) => handleDiscountTypeChange(item, e._id)}
                                                        inputSmall={true}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                                {item.discountType && item.discountType !== 'none' && (
                                                    <div className="flex items-center gap-0.5">
                                                        <InputField
                                                            extra="w-[50px]"
                                                            placeholder="0"
                                                            id={item.name + '_discount'}
                                                            type="number"
                                                            value={item.discountValue}
                                                            onChange={(e: any) => handleDiscountChange(item, e.target.value)}
                                                            inputSmall={true}
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="text-xs text-gray-600">
                                                            {item.discountType === 'percent' ? '%' : '€'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Total HT */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                                {getTotalHT([item]).toFixed(2)} €
                                            </p>
                                            {getMarginPct(item, getTotalHT([item])) && (
                                                <p className="text-xs font-medium text-gray-700">
                                                    Marge {getMarginPct(item, getTotalHT([item]))}
                                                </p>
                                            )}
                                        </td>

                                        {/* Action */}
                                        {!isDisabled && (
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <button
                                                    className="px-2 py-1 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                                                    onClick={() => onDelete(item)}
                                                >
                                                    Retirer
                                                </button>
                                            </td>
                                        )}
                                    </SortableTableRow>
                                    );
                                })}
                            </tbody>
                        </SortableContext>
                    </DndContext>
                </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="sm:hidden flex flex-col divide-y divide-gray-100">
                {refreshableOrder.items.map((item: any, index: number) => {
                    const q = searchQuery.trim().toLowerCase();
                    const isMatch = q !== '' && (
                        (item.name || '').toLowerCase().includes(q) ||
                        (item.ref || '').toLowerCase().includes(q)
                    );
                    return (
                    <div
                        key={`${item.name}-${index}-m`}
                        ref={(el) => { mobileRowRefs.current[index] = el; }}
                        className={`py-3 ${isMatch ? 'bg-yellow-50 rounded-lg px-2 ring-1 ring-yellow-300' : ''}`}
                    >
                        {/* Nom + réordonnancement + suppr */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-navy-700 truncate">{item.name}</p>
                                <p className="text-[11px] text-gray-600">{item.ref}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {!isDisabled && (
                                    <div className="flex flex-col gap-0.5">
                                        <button
                                            className="text-gray-400 hover:text-gray-600 disabled:opacity-20"
                                            disabled={index === 0}
                                            onClick={() => moveMobileItem(index, -1)}
                                            title="Monter"
                                        >
                                            ▲
                                        </button>
                                        <button
                                            className="text-gray-400 hover:text-gray-600 disabled:opacity-20"
                                            disabled={index === refreshableOrder.items.length - 1}
                                            onClick={() => moveMobileItem(index, 1)}
                                            title="Descendre"
                                        >
                                            ▼
                                        </button>
                                    </div>
                                )}
                                {!isDisabled && (
                                    <button
                                        className="px-2 py-1 text-xs text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                                        onClick={() => onDelete(item)}
                                    >
                                        Retirer
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Qté + prix */}
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <div className="flex flex-col gap-0.5">
                                {(() => {
                                    const fullProduct = products?.find((p: any) => p._id === item.productId);
                                    const isBillByBase = item.packagingBillByBaseUnit === true && item.packagingWeight;
                                    const weight = isBillByBase ? parseFloat(item.packagingWeight) : 1;
                                    const displayQty = isBillByBase ? parseFloat(item.quantity) * weight : item.quantity;
                                    return (
                                        <>
                                            <div className="flex items-center gap-1">
                                                <span className="text-xs text-gray-600">Qté :</span>
                                                <InputField
                                                    extra="w-[55px]"
                                                    inputSmall={true}
                                                    placeholder="Qté"
                                                    id={item.name + '_qty_m'}
                                                    type="number"
                                                    value={isBillByBase ? (displayQty % 1 === 0 ? displayQty : parseFloat(displayQty.toFixed(4))) : item.quantity}
                                                    onChange={(e: any) => {
                                                        if (isBillByBase) {
                                                            const baseVal = parseFloat(e.target.value);
                                                            handleQtyChange(item, isNaN(baseVal) ? 0 : baseVal / weight);
                                                        } else {
                                                            handleQtyChange(item, e.target.value);
                                                        }
                                                    }}
                                                    onClick={(e: any) => e.target.select()}
                                                    disabled={isDisabled}
                                                />
                                                <span className="text-sm font-medium text-gray-700">
                                                    {isBillByBase ? fullProduct?.unity : (item.packagingUnity || item.unity)}
                                                </span>
                                            </div>
                                            {isBillByBase && (
                                                <p className="text-[10px] text-gray-500">
                                                    {item.quantity} {item.packagingUnity} commandés
                                                </p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-600">PU :</span>
                                <InputField
                                    extra="w-[65px]"
                                    inputSmall={true}
                                    placeholder="Prix"
                                    id={item.name + '_up_m'}
                                    type="number"
                                    value={item.price}
                                    onChange={(e: any) => handlePriceChange(item, e.target.value)}
                                    disabled={isDisabled}
                                />
                                <span className="text-xs text-gray-600">€</span>
                            </div>
                            <p className="text-sm font-semibold text-navy-700">
                                = {getTotalHT([item]).toFixed(2)} €
                            </p>
                        </div>

                        {/* Lot + conditionnement */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <LotInput item={item} index={index} mobile products={products} isDisabled={isDisabled} handleLotNumberChange={handleLotNumberChange} />
                            <PackagingSelect item={item} />
                        </div>

                        <div className="mt-1">
                            <SavePriceHint item={item} />
                        </div>
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default OrderTable;
